import { useQuery } from '@tanstack/react-query';
import { authenticatedFetch } from '../utils';
import { usePreferences } from '../contexts/PreferencesContext';

/**
 * Query hooks for the v2 analytics API.
 *
 * TWO RULES SHAPE ALL OF THIS
 * ---------------------------
 * 1. **Every request carries the business timezone.** `tz` is not optional and is
 *    part of every query key, because a cached payload bucketed in one zone is
 *    wrong for another. Changing the timezone in Settings therefore invalidates
 *    the analytics cache rather than silently reusing UTC buckets.
 *
 * 2. **No `placeholderData: (prev) => prev`.** It looks like a smoothness win and
 *    is actually the stale-data bug: it puts the previous period's numbers under
 *    the new period's label, and if the new fetch then fails you are left holding
 *    confidently wrong figures. Screens show a skeleton on first load and dim the
 *    plot on a refetch, so the label and the data can never disagree.
 *
 * The Overview is ONE request (`useDashboard`). v1 made four, each paging the
 * whole row set, and the period label repainted before the charts did.
 */

const qs = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    search.append(k, String(v));
  });
  const s = search.toString();
  return s ? `?${s}` : '';
};

/**
 * Throw with the server's own words where it gave us any. The error EmptyState
 * renders `message`, so "Database connection issue. Please try again in a
 * moment." reaches the user instead of a generic failure.
 */
const request = async (path, params) => {
  const response = await authenticatedFetch(`${path}${qs(params)}`);
  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = body?.detail || body?.message || '';
    } catch {
      /* a non-JSON error body is not worth a second failure */
    }
    const err = new Error(detail || `Request failed (${response.status})`);
    err.status = response.status;
    throw err;
  }
  return response.json();
};

/** Shared cache policy. Analytics are a few minutes stale at worst and never auto-refetch. */
const ANALYTICS_OPTIONS = {
  staleTime: 2 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  retry: 1,
  // Deliberately off. /api/v2/dashboard pages the filtered row set into Python
  // with no aggregation pushdown; refetching that on every tab focus, times every
  // open dashboard, buys an outage rather than freshness. Freshness is a visible
  // "Synced HH:MM" stamp and a manual refresh instead.
  refetchOnWindowFocus: false,
};

/** The window every analytics hook needs, derived once. */
const useWindow = (filters) => {
  const { timeZone } = usePreferences();
  return {
    tz: timeZone,
    start_date: filters?.startDate || '',
    end_date: filters?.endDate || '',
  };
};

/**
 * The Overview screen, in one request: summary metrics with deltas and
 * denominators, the day-by-day series, the hour x weekday grid, the duration
 * histogram, categories, computed insights, and the data-health headline.
 */
export const useDashboard = (filters, options = {}) => {
  const params = useWindow(filters);
  return useQuery({
    queryKey: ['analytics', 'dashboard', params],
    queryFn: () => request('/api/v2/dashboard', params),
    ...ANALYTICS_OPTIONS,
    ...options,
  });
};

/** Customers to watch: key customers, priority leads and repeat callers, and whether we reached them. */
export const useWatchlist = (filters, options = {}) => {
  const params = useWindow(filters);
  return useQuery({
    queryKey: ['analytics', 'watchlist', params],
    queryFn: () => request('/api/v2/watchlist', params),
    ...ANALYTICS_OPTIONS,
    ...options,
  });
};

/** Callback SLA funnel, ageing worklist, and time-to-callback distribution. */
export const useCallbacks = (filters, options = {}) => {
  const params = useWindow(filters);
  return useQuery({
    queryKey: ['analytics', 'callbacks', params],
    queryFn: () => request('/api/v2/callbacks', params),
    ...ANALYTICS_OPTIONS,
    ...options,
  });
};

/** The scorecard: distribution, per-agent attainment, and the 12-week trend. */
export const useQuality = (filters, options = {}) => {
  const base = useWindow(filters);
  // Intelligence's segment filter (top layer / Call Category), when one is picked
  const params = filters?.segment ? { ...base, segment: filters.segment } : base;
  return useQuery({
    queryKey: ['analytics', 'quality', params],
    queryFn: () => request('/api/v2/quality', params),
    ...ANALYTICS_OPTIONS,
    ...options,
  });
};

/** Per-agent performance for the leaderboard and the volume x quality scatter. */
export const useAgentPerformance = (filters, options = {}) => {
  const params = useWindow(filters);
  return useQuery({
    queryKey: ['analytics', 'agents', params],
    queryFn: () => request('/api/v2/agents', params),
    ...ANALYTICS_OPTIONS,
    ...options,
  });
};

/**
 * Is data actually arriving? Independent of the selected period, because "your
 * provider stopped sending three days ago" is true regardless of which week the
 * user is looking at. Polls on a slow interval so a stall surfaces without the
 * user reloading.
 */
export const useDataHealth = (options = {}) => {
  const { timeZone } = usePreferences();
  return useQuery({
    queryKey: ['analytics', 'data-health', timeZone],
    queryFn: () => request('/api/v2/data-health', { tz: timeZone }),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true, // cheap, and the one place freshness matters most
    ...options,
  });
};

/**
 * Sidebar badge counts. Cheap count-only queries, so an owner can see whether
 * anything needs attention without visiting six pages.
 *
 * Takes the selected period: the open follow-ups badge counts items raised in
 * it, the same window the Follow-ups page lists by default.
 */
export const useNavCounts = (filters, options = {}) => {
  const params = useWindow(filters);
  return useQuery({
    queryKey: ['analytics', 'nav-counts', params],
    queryFn: () => request('/api/v2/nav-counts', params),
    staleTime: 2 * 60 * 1000,
    retry: 1,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

/**
 * Server-resolved analytics preferences. PreferencesContext reads tenant settings
 * directly for writes; this is the read path that carries the server's DEFAULTS
 * and each target's provenance, so a default is never shown as a tenant choice.
 */
export const useAnalyticsPreferences = (options = {}) =>
  useQuery({
    queryKey: ['analytics', 'preferences'],
    queryFn: () => request('/api/v2/preferences', {}),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });

/**
 * Predicates for QueryState's `isEmpty`. Emptiness is domain-specific and the
 * distinction that matters is "the period had no calls" versus "the request
 * failed" — QueryState handles the second, these describe the first.
 */
export const isDashboardEmpty = (data) => !data || (data?.summary?.total_calls?.numerator ?? 0) === 0;
export const hasNoCallbacks = (data) => !data || (data?.distinct_missed_numbers ?? 0) === 0;
export const isQualityUnconfigured = (data) => !data || data.configured === false;
